import qs from 'qs';

export const cleanPath = (path: string | number): string => `${path}`.replace(/^\/+|\/+$/g, '');

type PathParams = Record<string, string | number>;

export const applyPathParams = (base: string, params: PathParams = {}) => {
	const copied = { ...params };

	Object.entries(params).forEach(([key, value]) => {
		if (base.includes(`:${key}`)) {
			base = base.replaceAll(`:${key}`, `${value}`);
			delete copied[key];
		}
	});

	if (Object.keys(copied).length) {
		const searchParams = qs.stringify(copied);
		base = `${base}?${searchParams}`;
	}

	return base;
};

export const joinPath = (...paths: (string | number)[]): string => paths.map(cleanPath).join('/');

export const buildURL =
	(base = '') =>
	(...paths: (string | number)[]): string =>
		joinPath(base, ...paths);

export type ParseSearchParamsConfig<P extends readonly string[]> = {
	allowedValues?: Partial<Record<P[number], readonly string[]>>;
	defaultValue?: Partial<Record<P[number], string>>;
	transforms?: Partial<Record<P[number], (value: string) => any>>;
};

type ParseSearchParamsResult<P extends readonly string[], C extends ParseSearchParamsConfig<P>> = {
	[K in P[number] as K extends keyof NonNullable<C['defaultValue']> ? K : never]: ParsedValue<K, C>;
} & {
	[K in P[number] as K extends keyof NonNullable<C['defaultValue']> ? never : K]?: ParsedValue<K, C>;
};

type ParsedValue<K extends string, C extends ParseSearchParamsConfig<readonly string[]>> =
	// Check if there's a transform for this key
	K extends keyof NonNullable<C['transforms']>
		? // There is a transform
			K extends keyof NonNullable<C['defaultValue']>
			? // Transform + Default = Transform return type (never undefined)
				NonNullable<C['transforms']>[K] extends (value: string) => infer R
				? R
				: string | undefined
			: // Transform + No Default = Transform return type | undefined
				NonNullable<C['transforms']>[K] extends (value: string) => infer R
				? R | undefined
				: string | undefined
		: // No transform
			K extends keyof NonNullable<C['defaultValue']>
			? // No Transform + Default = string (never undefined)
				string
			: // No Transform + No Default = string | undefined
				string | undefined;

export const parseSearchParams = <const P extends readonly string[], C extends ParseSearchParamsConfig<P>>(
	search: URLSearchParams,
	params: P,
	config: Partial<C> = {}
): ParseSearchParamsResult<P, C> => {
	const { allowedValues, defaultValue, transforms } = config;

	return params.reduce(
		(acc, param) => {
			const value = search.get(param) || defaultValue?.[param as P[number]] || '';
			const allowed = allowedValues?.[param as P[number]];

			if (allowed && !allowed.includes(value)) {
				return acc;
			}

			const transform = transforms?.[param as P[number]];
			const transformedValue = transform ? transform(value) : value;

			return { ...acc, [param]: transformedValue };
		},
		{} as ParseSearchParamsResult<P, C>
	);
};
